import { useState } from "react";
import { useQuery } from "@apollo/client";
import useCapabilities, { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useNavigate } from "@/router";
import usePageTitle from "@/hooks/usePageTitle";
import usePagination from "@/hooks/usePagination";
import { PERSON_BILLING_ORDERS_QUERY } from "./queries";
import { Controller } from "react-hook-form";
import { OrderStage } from "@/pages/billing/types";
import { Variables } from "@/pages/billing/orders/types";
import { OrderRow } from "@/pages/billing/types";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { toFormat } from "@/lib/dateHelpers";
import getActiveFilters from "@/lib/filterHelpers";
import { renderStatus } from "@/pages/billing/_utils";
import EmptyState from "@/components/EmptyState";
import Icon from "@/components/Icon";
import MultiSelect from "@/components/MultiSelect";
import Table, { type Row, TableControls } from "@/components/Table";
import { useParams } from "react-router-dom";
import CreateOrderModal from "@/pages/billing/orders/_components/CreateOrderModal";
import { useForm } from "react-hook-form";
import { Path } from "@/router";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { ProductTable } from "@/features/billing/components/ProductTable";

const mapCommerceOrders = (data: any[]) =>
  (data || []).map(({ updatedAt, dueDate, balance, lineItems, ...order }) => ({
    ...order,
    personId: order.person.id,
    personName: `${order.person.firstName} ${order.person.lastName}`,
    updatedAt: formatDate(updatedAt),
    dueDate: formatDate(dueDate),
    balance: centsToDollarsFormatted(balance) || undefined,
    products: lineItems.length,
    lineItems: lineItems
  }));

const formatDate = (date?: string): string | undefined => (date && toFormat(date)) || undefined;

export default function OrdersPage() {
  usePageTitle("/billing/orders", "Billing");
  useRequireCapability({ domain: DOMAIN.COMMERCE, permission: PERMISSION.viewOrders });
  const { pgaId } = useParams();
  const { renderFooter, paginationVariables, resetPagination } = usePagination();
  const [selectedStatusValues, setSelectedStatusValues] = useState<string[]>([]);
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState<boolean>(false);
  const { hasPermission } = useCapabilities();
  const defaultVariables = { query: pgaId };
  const { isCompactLayout: isMobile } = useIsCompactLayout();

  const [variables, setVariables] = useState<Variables>(defaultVariables);
  const { data, loading } = useQuery(PERSON_BILLING_ORDERS_QUERY, {
    variables: { ...variables, ...paginationVariables }
  });

  const navigate = useNavigate();

  const pattern = "/billing/orders";
  function onClickRow(row: Row<OrderRow>) {
    navigate(`${pattern}/${row.original.id}?return_to=/people/${pgaId}/billing/orders` as Path);
  }

  const { control } = useForm();
  const orderData: OrderRow[] = mapCommerceOrders(data?.commerceOrders.nodes || []);

  const filters = {
    status: selectedStatusValues.length > 0
  };
  const { isActive } = getActiveFilters(filters, filters, true);

  return (
    <>
      <div className="w-full">
        <div className="pt-12" />
        <Table
          data={orderData || []}
          loading={loading}
          enableRowSelection={(row) => row.original.payable || row.original.status === OrderStage.DRAFT}
          onClickRow={onClickRow}
          renderExpandedRow={({ row }) => <ProductTable row={row} />}
          hasActiveFilters={isActive}
          renderControls={(_table, { onSortClick } = {}) => (
            <TableControls
              actions={
                hasPermission(PERMISSION.manageOrders)
                  ? [
                      {
                        text: "Create Order",
                        iconName: "add",
                        variant: "filled",
                        onClick: () => setIsCreateOrderModalOpen(true)
                      }
                    ]
                  : []
              }
              useExpandedFilters
              expandedFiltersState={{ status: selectedStatusValues.length > 0 }}
              onClearAllFilters={() => {
                setVariables(defaultVariables);
                setSelectedStatusValues([]);
                resetPagination();
              }}
              onSortClick={onSortClick}
              activeFilters={selectedStatusValues.length}
            >
              <div data-name="status">
                <Controller
                  name="status"
                  control={control}
                  render={() => (
                    <MultiSelect
                      label="Stage"
                      selectedValues={selectedStatusValues}
                      options={[
                        { value: "canceled", label: "Canceled" },
                        { value: "draft", label: "Draft" },
                        { value: "open", label: "Open" },
                        { value: "paid", label: "Paid" },
                        { value: "pending", label: "Pending" },
                        { value: "uncollectible", label: "Uncollectible" }
                      ]}
                      onSelect={(value, checked) => {
                        const status = checked
                          ? [...selectedStatusValues, value]
                          : selectedStatusValues.filter((v) => v !== value);
                        setSelectedStatusValues(status);
                        setVariables((prevValue) => ({
                          ...prevValue,
                          statuses: status
                        }));
                        resetPagination();
                      }}
                      onClear={() => {
                        setSelectedStatusValues([]);
                        setVariables((prevValue) => ({
                          ...prevValue,
                          statuses: []
                        }));
                      }}
                      className="w-full"
                    />
                  )}
                />
              </div>
            </TableControls>
          )}
          columns={[
            { accessorKey: "description", header: "Description", enableSorting: false },
            {
              accessorKey: "products",
              header: "Products",
              enableSorting: false,
              cell: ({ row }) => {
                return (
                  <div
                    className="flex items-center gap-2 py-4 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      row.getToggleExpandedHandler()();
                    }}
                  >
                    <span>{row.original.lineItems?.length}</span>
                    {!isMobile && <Icon name={row.getIsExpanded() ? "expand_less" : "expand_more"} />}
                  </div>
                );
              }
            },
            { accessorKey: "updatedAt", header: "Modified", enableSorting: false },
            { accessorKey: "dueDate", header: "Due Date", enableSorting: false },
            { accessorKey: "balance", header: "Balance", enableSorting: false },
            {
              header: "Stage",
              id: "stage",
              cell: ({ row: { original } }) => renderStatus(original.status, original.paymentStatus),
              size: 100
            }
          ]}
          renderFilteredEmptyState={() => (
            <EmptyState title="No Orders" caption="There are no past or current orders." iconName="receipt_long" />
          )}
          renderFooter={() => (
            <div className={`justify-end flex flex-col md:flex-row md:items-center`}>
              {renderFooter(data?.commerceOrders, {
                totalCount: data?.commerceOrders.totalCount || 0,
                pageCount: orderData.length
              })()}
            </div>
          )}
        />
      </div>
      {isCreateOrderModalOpen && <CreateOrderModal onDismiss={() => setIsCreateOrderModalOpen(false)} pgaId={pgaId} />}
    </>
  );
}
